import { useOnboardingStore } from "@/stores/onboarding-store";
import { getWindowRoleSync } from "@/desktop/use-window-role";
import {
  getDesktopChatAuthScope,
  subscribeDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";
import { subscribeOwnerChanged } from "./owner-state-client";
import { isTauri } from "./is-tauri";
import type { SystemToast } from "@/stores/notification-types";

type Notice = { toast: SystemToast; action?: () => void };
type Surface = {
  show: (toast: SystemToast, action?: () => void) => void;
  takeVisible: () => Notice[];
  readQueued: () => SystemToast[];
  writeQueued: (toasts: SystemToast[]) => void;
};
const queued = new Map<string, Notice>();
let surface: Surface | null = null;
let refreshingTrial: (() => Promise<void>) | null = null;
let releasing = false;
let publishing = false;
let currentId: string | null = null;
let generation = 0;
const trialRefreshWaiters = new Set<() => void>();
const presentedListeners = new Set<(toast: SystemToast) => void>();

/** Unknown boot state also stays quiet until the onboarding decision arrives. */
export function onboardingSystemNoticesBlocked(): boolean {
  if (!isTauri()) return false;
  const state = useOnboardingStore.getState();
  return (
    !state.stepHydrated ||
    state.restarting ||
    (state.step !== "completed" && state.step !== "skipped")
  );
}
export function useOnboardingSystemNoticesBlocked(): boolean {
  return useOnboardingStore(
    (state) =>
      isTauri() &&
      (!state.stepHydrated ||
        state.restarting ||
        (state.step !== "completed" && state.step !== "skipped")),
  );
}
export function subscribeSystemNoticePresented(
  listener: (toast: SystemToast) => void,
): () => void {
  presentedListeners.add(listener);
  return () => {
    presentedListeners.delete(listener);
  };
}
/** Once-only notices whose "shown" flag is written when they actually appear. */
const ONCE_ONLY_NOTICE_PREFIXES = [
  "denker.trial-notice.",
  "denker.onscreen-limit.",
];

export function systemNoticePresented(toast: SystemToast): void {
  if (publishing || (isTauri() && getWindowRoleSync() !== "owner")) return;
  if (ONCE_ONLY_NOTICE_PREFIXES.some((prefix) => toast.id.startsWith(prefix))) {
    try {
      localStorage.setItem(toast.id, "1");
    } catch {
      /* In-memory dedup remains available. */
    }
  }
  for (const listener of presentedListeners) listener(toast);
}
/** True while the gate re-shows a queued notice, which keeps its revision. */
export function isPublishingDeferredSystemNotice(): boolean {
  return publishing;
}
export function getDeferredSystemNotice(id: string): SystemToast | undefined {
  return queued.get(id)?.toast;
}
export function hasDeferredSystemNotice(id: string): boolean {
  return queued.has(id);
}
function publishQueue(): void {
  if (!isTauri() || getWindowRoleSync() === "owner")
    surface?.writeQueued([...queued.values()].map(({ toast }) => toast));
}
function recoverQueue(): void {
  const previous = new Map(queued);
  queued.clear();
  for (const toast of surface?.readQueued() ?? []) {
    const retained = previous.get(toast.id);
    queued.set(toast.id, {
      toast,
      action:
        retained?.toast.revision === toast.revision
          ? retained?.action
          : undefined,
    });
  }
}
export function deferOnboardingSystemNotice(
  toast: SystemToast,
  action?: () => void,
): boolean {
  if (
    publishing ||
    (!onboardingSystemNoticesBlocked() &&
      !releasing &&
      !currentId &&
      !queued.size)
  )
    return false;
  queued.set(toast.id, { toast, action });
  publishQueue();
  return true;
}
export function removeDeferredSystemNotice(
  id: string,
  revision?: string | null,
): boolean {
  const notice = queued.get(id);
  if (
    !notice ||
    (revision !== undefined && (notice.toast.revision ?? null) !== revision)
  )
    return false;
  queued.delete(id);
  publishQueue();
  return true;
}
export function discardDeferredTrialNotices(userId: string): string[] {
  const removed: string[] = [];
  for (const id of queued.keys())
    if (id.startsWith(`denker.trial-notice.${userId}.`)) {
      queued.delete(id);
      removed.push(id);
    }
  publishQueue();
  return removed;
}
export function systemNoticeRemoved(id: string): void {
  if (currentId !== id) return;
  currentId = null;
  queueMicrotask(drain);
}
export function registerOnboardingTrialRefresh(
  refresh: () => Promise<void>,
): () => void {
  refreshingTrial = refresh;
  for (const ready of trialRefreshWaiters) ready();
  return () => {
    if (refreshingTrial === refresh) refreshingTrial = null;
  };
}
function drain(): void {
  if (
    !surface ||
    releasing ||
    currentId ||
    onboardingSystemNoticesBlocked() ||
    (isTauri() && getWindowRoleSync() !== "owner")
  )
    return;
  const notice =
    [...queued.values()].find(({ toast }) => /\.started$/.test(toast.id)) ??
    queued.values().next().value;
  if (!notice) return;
  queued.delete(notice.toast.id);
  publishQueue();
  currentId = notice.toast.id;
  publishing = true;
  try {
    surface.show(notice.toast, notice.action);
  } finally {
    publishing = false;
  }
  systemNoticePresented(notice.toast);
}
async function releaseAfterTrialRefresh(): Promise<void> {
  if (onboardingSystemNoticesBlocked() || getWindowRoleSync() !== "owner")
    return;
  const revision = ++generation;
  releasing = true;
  try {
    if (!refreshingTrial && getDesktopChatAuthScope()) {
      await new Promise<void>((resolve) => {
        const ready = () => {
          clearTimeout(timer);
          trialRefreshWaiters.delete(ready);
          resolve();
        };
        const timer = setTimeout(ready, 2000);
        trialRefreshWaiters.add(ready);
      });
    }
    if (revision !== generation || onboardingSystemNoticesBlocked()) return;
    if (refreshingTrial) await refreshingTrial();
    else {
      // Views without billing still release updates, never an unverified trial.
      for (const id of queued.keys())
        if (id.startsWith("denker.trial-notice.")) queued.delete(id);
      publishQueue();
    }
  } catch {
    for (const id of queued.keys())
      if (id.startsWith("denker.trial-notice.")) queued.delete(id);
    publishQueue();
  } finally {
    if (generation === revision && !onboardingSystemNoticesBlocked()) {
      releasing = false;
      drain();
    }
  }
}
export function installOnboardingSystemNoticeGate(target: Surface): void {
  surface = target;
  if (!isTauri()) return;
  let blocked = onboardingSystemNoticesBlocked();
  useOnboardingStore.subscribe(() => {
    const next = onboardingSystemNoticesBlocked();
    if (next === blocked) return;
    blocked = next;
    ++generation;
    if (next) {
      releasing = false;
      currentId = null;
      for (const notice of target.takeVisible())
        if (!queued.has(notice.toast.id)) queued.set(notice.toast.id, notice);
      publishQueue();
      return;
    }
    void releaseAfterTrialRefresh();
  });
  subscribeDesktopChatAuthScope(() => {
    generation += 1;
    releasing = false;
    currentId = null;
    for (const notice of target.takeVisible())
      if (notice.toast.appWide && !queued.has(notice.toast.id))
        queued.set(notice.toast.id, notice);
    for (const [id, notice] of queued)
      if (!notice.toast.appWide) queued.delete(id);
    publishQueue();
    void releaseAfterTrialRefresh();
  });
  subscribeOwnerChanged(() =>
    queueMicrotask(() => {
      ++generation;
      releasing = false;
      if (getWindowRoleSync() !== "owner") return;
      recoverQueue();
      currentId = null;
      const pending = new Map(queued);
      queued.clear();
      for (const notice of target.takeVisible())
        queued.set(notice.toast.id, pending.get(notice.toast.id) ?? notice);
      for (const [id, notice] of pending)
        if (!queued.has(id)) queued.set(id, notice);
      publishQueue();
      void releaseAfterTrialRefresh();
    }),
  );
  recoverQueue();
  if (!blocked && queued.size) void releaseAfterTrialRefresh();
}
